import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Skeleton,
  Grid,
  Stack
} from '@tonic/shared/components/ui';
import { FileText, ArrowLeft, Edit, Archive, Share2, MoreVertical } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/format';

// Mock data for the BILLY Bookcase resale item
const mockResaleItem = {
  id: "RS-BILLY001",
  name: "BILLY Bookcase",
  description: "White bookcase, minor wear on corners. Perfect for home office or living room storage.",
  category: "Storage",
  status: "Listed",
  condition: "Good",
  originalPrice: 79.99,
  resalePrice: 39.99,
  profitMargin: 42,
  dimensions: "80x28x202 cm",
  weight: "28 kg",
  material: "Particleboard, Paper foil",
  color: "White",
  brand: "IKEA",
  model: "BILLY-80263832",
  manufacturingDate: "2023-06-15",
  listedAt: "2024-03-15",
  location: "Warehouse A - Section 12B",
  timeListed: 5, // days
  views: 145,
  saves: 12,
  inquiries: 3,
  carbonSavings: 12.5,
  repairCost: 0,
  sustainabilityScore: 85,
  images: [
    {
      url: "https://www.ikea.com/us/en/images/products/billy-bookcase-white__0625599_PE692385_S5.JPG",
      type: "primary",
      uploadedAt: "2024-03-15"
    },
    {
      url: "https://www.ikea.com/us/en/images/products/billy-bookcase-white__0644571_PE702939_S5.JPG",
      type: "secondary",
      uploadedAt: "2024-03-15"
    }
  ],
  history: [
    {
      date: "2024-03-15T14:30:00Z",
      title: "Item Listed",
      description: "Item has been listed for resale"
    },
    {
      date: "2024-03-14T16:45:00Z",
      title: "Quality Check Completed",
      description: "Item passed quality inspection"
    },
    {
      date: "2024-03-14T11:20:00Z",
      title: "Item Received",
      description: "Item received at warehouse"
    }
  ],
  documents: [
    {
      name: "Quality Assessment Report",
      type: "PDF",
      size: "1.2 MB",
      uploadedAt: "2024-03-14T16:45:00Z"
    },
    {
      name: "Original Assembly Instructions",
      type: "PDF",
      size: "2.4 MB",
      uploadedAt: "2024-03-14T11:20:00Z"
    }
  ],
  qualityChecklist: {
    structural: "Pass",
    cosmetic: "Pass",
    functionality: "Pass",
    safety: "Pass",
    cleanliness: "Pass",
    notes: "Minor wear on corners, all parts present and functional"
  }
};

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-24" />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-32 mb-1" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-6 w-48" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ResaleDetailPage() {
  const { id } = useParams();
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const item = mockResaleItem; // In a real app, this would come from an API call

  if (!item) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-3">
              <h3 className="text-lg font-semibold">Item Not Found</h3>
              <p className="text-sm text-muted-foreground">The requested item could not be found.</p>
              <Button variant="outline" onClick={() => window.history.back()}>
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{item.name}</h1>
            <p className="text-sm text-muted-foreground">ID: {item.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          <Button variant="outline" size="sm">
            <Archive className="h-4 w-4 mr-2" />
            Archive
          </Button>
          <Button onClick={() => setIsEditDialogOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resale Price</CardTitle>
            <Badge variant={item.status === 'Listed' ? 'default' : 'secondary'}>
              {item.status}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(item.resalePrice)}</div>
            <p className="text-xs text-muted-foreground">
              Original Price: {formatCurrency(item.originalPrice)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time Listed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{item.timeListed} days</div>
            <p className="text-xs text-muted-foreground">
              Listed on {formatDate(item.listedAt)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{item.views} views</div>
            <p className="text-xs text-muted-foreground">
              {item.saves} saves • {item.inquiries} inquiries
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Images */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Images</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {item.images.map((image, index) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden">
                  <img
                    src={image.url}
                    alt={`${item.name} - ${image.type}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right Column - Details */}
        <Card>
          <CardHeader>
            <CardTitle>Item Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Category</dt>
                <dd className="mt-1">{item.category}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Condition</dt>
                <dd className="mt-1">{item.condition}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Dimensions</dt>
                <dd className="mt-1">{item.dimensions}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Material</dt>
                <dd className="mt-1">{item.material}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Location</dt>
                <dd className="mt-1">{item.location}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Model</dt>
                <dd className="mt-1">{item.model}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-0">
          <Tabs defaultValue="quality" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none">
              <TabsTrigger value="quality">Quality Check</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="sustainability">Sustainability</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            <div className="p-4">
              <TabsContent value="quality">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {Object.entries(item.qualityChecklist).map(([key, value]) => (
                      key !== 'notes' && (
                        <div key={key} className="space-y-1">
                          <p className="text-sm font-medium capitalize">{key}</p>
                          <Badge variant={value === 'Pass' ? 'default' : 'destructive'}>
                            {value}
                          </Badge>
                        </div>
                      )
                    ))}
                  </div>
                  <div className="pt-4">
                    <p className="text-sm font-medium">Notes</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {item.qualityChecklist.notes}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="history">
                <div className="space-y-4">
                  {item.history.map((event, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                      <div>
                        <p className="font-medium">{event.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(event.date)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="sustainability">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium">Carbon Savings</p>
                      <p className="text-2xl font-bold mt-1">{item.carbonSavings} kg</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Sustainability Score</p>
                      <p className="text-2xl font-bold mt-1">{item.sustainabilityScore}/100</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="documents">
                <div className="space-y-4">
                  {item.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {doc.size} • {formatDate(doc.uploadedAt)}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
